import { Dialog } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getPlatform, getHostPrefix } from "../../utils/CommonFunctions";
import usePWADetection from "../../hooks/usePWADetection";
import { ReactComponent as CloseIcon } from "../../assets/icon_close.svg";
import { ReactComponent as ShareIcon } from "../../assets/icon_share_ios.svg";
import { ReactComponent as InfoIcon } from "../../assets/icon_info.svg";
import { ReactComponent as InfoIconIOS } from "../../assets/icon_info_ios.svg";
import { ReactComponent as ThreeDotsIcon } from "../../assets/icon_three_dots.svg";


interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PWAInstallPrompt = () => {
  const [modalOpen, setModalOpen] = useState(true);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const platform = getPlatform();
  const { isPWA } = usePWADetection();
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [t] = useTranslation();
  const appInstalled = useCallback(() => {
    setIsAppInstalled(true);
  }, []);

  const currentHost = getHostPrefix();
  const manifestMap = window.config?.manifestMap || {};
  const isPwaConfigured = Object.keys(manifestMap).some(host => currentHost.includes(host));

  const iosDownloadMap = window.config?.ios_download || {};
  const iosDownloadHost = Object.keys(iosDownloadMap).find(host => currentHost.includes(host));
  const iosDownloadLink = iosDownloadHost ? iosDownloadMap[iosDownloadHost] : undefined;
  const showAppStoreDownload = platform.os === "iOS" && !!iosDownloadLink;

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", appInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", appInstalled);
    };
  }, [appInstalled]);

  const onInstall = () => {
    if (deferredPrompt.current) {
      deferredPrompt?.current.prompt();
      deferredPrompt.current.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          appInstalled();
          deferredPrompt.current = null;
        }
      });
    }
  };

  if (platform.native || isPWA || isAppInstalled) return null;
  if (!isPwaConfigured && !showAppStoreDownload) return null;
  return (
    <Dialog open={modalOpen}>
      <div className="pwa-prompt-container">
        <button onClick={() => setModalOpen(false)} className="close-button">
          <CloseIcon />
        </button>
        <div className="content">
          <h3>{t("PWA.install_prompt.title")}</h3>
          <p>
            {showAppStoreDownload
              ? t("PWA.install_prompt.ios-download-description")
              : t("PWA.install_prompt.description")}
          </p>
        </div>
        {showAppStoreDownload ? (
          <div className="device-instructions-app-store">
            <a href={iosDownloadLink} target="_blank" rel="noopener noreferrer">
              {t("PWA.install_prompt.action-app-store")}
            </a>
          </div>
        ) : (
        <div className={`device-instructions-${platform.os.toLowerCase()}`}>

          {platform.os === "Android" && (
            <button onClick={onInstall}>{t("PWA.install_prompt.action-android")}</button>
          )}
          {platform.os === "iOS" && (
          <>
            <div>
                <h3>
                  {t("PWA.install_prompt.action-ios-before")}
                  <ShareIcon />
                  {t("PWA.install_prompt.action-ios-after")}
                </h3>

              <p>
                <Trans i18nKey="PWA.install_prompt.three-dots-before" components={{ strong: <strong /> }} />
                <ThreeDotsIcon />
                <span>
                  <Trans i18nKey="PWA.install_prompt.three-dots-after" components={{ strong: <strong /> }} />
                </span>
              </p>
            </div>

            <div className="for-more-info">
              <InfoIconIOS />
              <p>
                <Trans i18nKey="PWA.install_prompt.how-to-install" components={{ strong: <strong /> }} />
              </p>
            </div>
          </>
          )}
          {platform.os === "unknown" && (
            <>
              <InfoIcon />
              <span>
                <Trans i18nKey="PWA.install_prompt.action-unknown" components={{ strong: <strong /> }} />
              </span>
            </>
          )}
        </div>
        )}
      </div>
    </Dialog>
  );
};

export default PWAInstallPrompt;
